import type { FetchResponseI } from 'types';
import { fetchData } from 'Utils/fetch-data';

/** Las dos pestañas de la sección Miembros. */
export type MembershipTab = 'mandatory' | 'optional';

/** Un miembro tal como lo devuelve el backend en `data`. */
export interface CourseMemberI {
  userId: string;
  fullName: string;
  payrollNum: number | null;
  departmentName: string | null;
  jobTitleName: string | null;
  terminalName: string | null;
  membership: MembershipTab;
  /**
   * Solo en la pestaña de obligatorios: `'manual'` si se le dio de alta a mano,
   * `'assigned'` si lo trae su departamento o su puesto. En opcionales, `null`.
   */
  origin: 'manual' | 'assigned' | null;
  /** `true` si ya tiene fila de inscripción. Es lo que impide quitarle la invitación. */
  hasEnrollment: boolean;
  /**
   * Estado que pinta la quinta columna en opcionales. `'not_started'` cubre
   * tanto a quien no tiene fila como a quien la tiene sin avance: la fila nace
   * en "Por Cursar" y por sí sola no significa que haya empezado.
   */
  state: 'not_started' | 'in_progress' | 'approved' | 'failed';
}

/** Conteos de las dos pestañas y estado de opcionalidad del curso. */
export interface CourseMembersMetaI {
  mandatory: number;
  optional: number;
  isOptional: boolean;
  optionalScope: 'all' | 'members' | null;
}

/** Filtros de servidor del listado. */
export interface CourseMembersFilterI {
  membership: MembershipTab;
  search?: string;
  departmentId?: string;
  jobTitleId?: string;
  terminalId?: string;
  page: number;
  pageSize: number;
}

// `baseURL` ya incluye `/api`, así que aquí solo va el recurso.
const endpoint = (courseId: string): string => `/courses/${courseId}/members`;

/**
 * Cliente REST de los miembros de un curso.
 *
 * Usa `fetchData`, que adjunta el token Bearer y lee el envelope `{ data }`.
 * Las tres escrituras son masivas: reciben un arreglo de ids, nunca uno solo.
 *
 * El backend responde 409 con `code: 'optional_scope_all'` cuando se intenta
 * tocar la lista de opcionales de un curso abierto a toda la empresa.
 */
const courseMembersApi = {
  readAll: (courseId: string, filters: CourseMembersFilterI): Promise<FetchResponseI<CourseMemberI[]>> =>
    fetchData<CourseMemberI[]>(endpoint(courseId), {
      method: 'GET',
      params: filters,
      includeJsonApiMeta: true,
      includePagination: true,
    }),

  add: (
    courseId: string,
    userIds: string[],
    membership: MembershipTab
  ): Promise<FetchResponseI<{ added: number; skipped: number }>> =>
    fetchData(endpoint(courseId), { method: 'POST', data: { userIds, membership } }),

  remove: (courseId: string, userIds: string[]): Promise<FetchResponseI<{ removed: number }>> =>
    fetchData(endpoint(courseId), { method: 'DELETE', data: { userIds } }),

  moveToMandatory: (courseId: string, userIds: string[]): Promise<FetchResponseI<{ moved: number }>> =>
    fetchData(endpoint(courseId), { method: 'PATCH', data: { userIds, membership: 'mandatory' } }),
};

export default courseMembersApi;
